import { RefreshCw, Sparkles } from 'lucide-react'

export interface WorkerProgressBannerProps {
  type: 'import' | 'enrichment' | 'search' | 'custom'
  message: string
  subtitle?: string
  progress?: {
    processed: number
    total: number
  }
  onDismiss?: () => void
}

/**
 * Reusable banner component for displaying background worker progress
 *
 * Features:
 * - Different visual styles for different worker types
 * - Optional progress display (X/Y processed)
 * - Dismissible with optional callback
 * - Spinning icon animation
 */
export default function WorkerProgressBanner({
  type,
  message,
  subtitle,
  progress,
  onDismiss
}: WorkerProgressBannerProps) {
  // Color schemes for different worker types
  const getColorScheme = () => {
    switch (type) {
      case 'import':
        return {
          bg: 'bg-blue-50',
          border: 'border-blue-200',
          textPrimary: 'text-blue-900',
          textSecondary: 'text-blue-700',
          icon: 'text-blue-600',
          dismissHover: 'hover:text-blue-800'
        }
      case 'enrichment':
        return {
          bg: 'bg-purple-50',
          border: 'border-purple-200',
          textPrimary: 'text-purple-900',
          textSecondary: 'text-purple-700',
          icon: 'text-purple-600',
          dismissHover: 'hover:text-purple-800'
        }
      case 'search':
        return {
          bg: 'bg-green-50',
          border: 'border-green-200',
          textPrimary: 'text-green-900',
          textSecondary: 'text-green-700',
          icon: 'text-green-600',
          dismissHover: 'hover:text-green-800'
        }
      case 'custom':
      default:
        return {
          bg: 'bg-gray-50',
          border: 'border-gray-200',
          textPrimary: 'text-gray-900',
          textSecondary: 'text-gray-700',
          icon: 'text-gray-600',
          dismissHover: 'hover:text-gray-800'
        }
    }
  }

  const colors = getColorScheme()

  // Icon for different worker types
  const getIcon = () => {
    switch (type) {
      case 'import':
        return <RefreshCw className={`h-5 w-5 ${colors.icon} animate-spin`} />
      case 'enrichment':
        return <Sparkles className={`h-5 w-5 ${colors.icon} animate-pulse`} />
      case 'search':
        return <RefreshCw className={`h-5 w-5 ${colors.icon} animate-spin`} />
      default:
        return <RefreshCw className={`h-5 w-5 ${colors.icon} animate-spin`} />
    }
  }

  // Format message with progress if available
  const getDisplayMessage = () => {
    if (progress) {
      const { processed, total } = progress
      return `${message} (${processed}/${total} processed)`
    }
    return message
  }

  // Default subtitle based on type
  const getDefaultSubtitle = () => {
    switch (type) {
      case 'import':
        return 'This may take a few minutes. You can continue working while content is being imported.'
      case 'enrichment':
        return 'Generating summaries, keywords, benefits, and key points...'
      case 'search':
        return 'Executing search queries across multiple AI providers...'
      default:
        return 'Processing in background...'
    }
  }

  return (
    <div className={`${colors.bg} border ${colors.border} rounded-lg p-4 flex items-center justify-between`}>
      <div className="flex items-center space-x-3">
        {getIcon()}
        <div>
          <p className={`text-sm font-medium ${colors.textPrimary}`}>
            {getDisplayMessage()}
          </p>
          {(subtitle !== '' || subtitle === undefined) && (
            <p className={`text-xs ${colors.textSecondary} mt-1`}>
              {subtitle || getDefaultSubtitle()}
            </p>
          )}
        </div>
      </div>
      {onDismiss && (
        <button
          onClick={onDismiss}
          className={`${colors.icon} ${colors.dismissHover}`}
          title="Dismiss (processing will continue in background)"
        >
          ✕
        </button>
      )}
    </div>
  )
}
